<template>
  <section class="payments-page payments-list-page supplier-payments-typography" aria-label="Payments">
    <div class="payment-list-toolbar">
      <nav class="payment-list-modes" aria-label="Payment type">
        <button type="button" :class="{ active: mode === 'subcontractors' }" :aria-pressed="mode === 'subcontractors'" @click="mode = 'subcontractors'">Subcontractors</button>
        <button v-if="employeesEnabled" type="button" :class="{ active: mode === 'employees' }" :aria-pressed="mode === 'employees'" @click="mode = 'employees'">Employees</button>
      </nav>
    </div>
    <SupplierInvoicesView v-if="mode === 'subcontractors'" embedded />
    <template v-else>
      <div class="payment-list-toolbar">
        <button type="button" class="payment-list-button" @click="openSupplierPayment()"><AppointmentIcon name="plus" />New payment</button>
        <PaymentListFilters :filters="filters" :options="employeeOptions" :statuses="statuses" entity-label="Employee" @change="updateFilters" />
      </div>
      <div class="payment-list-content" :aria-busy="loading">
        <p v-if="error" role="alert" class="payment-list-message payment-error">{{ error }}</p>
        <span v-if="loading && items.length" role="status" class="sr-only">Loading payments…</span>
        <div class="payment-list-scroll">
          <table class="payment-list-table employee-payments-table" aria-label="Employee payments">
            <thead><tr><th>Payment #</th><th>Employee</th><th>Period</th><th class="numeric">Hours</th><th class="numeric">Amount</th><th>Status</th><th>Paid date</th></tr></thead>
            <tbody>
              <tr v-for="item in items" :key="item.documentId" class="payment-list-row" @click="openSupplierPayment({ paymentId: item.documentId })">
                <td><button type="button" class="payment-number-button" @click.stop="openSupplierPayment({ paymentId: item.documentId })">{{ item.number }}</button></td>
                <td>{{ item.supplier?.name || '—' }}</td>
                <td>{{ formatDate(item.dateFrom) }} – {{ formatDate(item.dateTo) }}</td>
                <td class="numeric">{{ item.totalHours }}</td><td class="numeric amount">{{ money(item.totalAmount) }}</td>
                <td><span class="payment-list-status" :class="`is-${item.status}`">{{ item.status }}</span></td>
                <td>{{ formatDate(item.paidDate) }}</td>
              </tr>
              <tr v-if="!items.length && (loading || !error)">
                <td colspan="7" class="payment-empty">
                  <span v-if="loading" role="status">Loading payments…</span>
                  <span v-else>No employee payments found for current filters.</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <PaymentPagination v-if="items.length || meta.total" :page="page" :page-count="meta.pageCount || 1" :page-size="meta.pageSize || 50" :total="meta.total || 0" :loading="loading" @change="changePage" />
      </div>
    </template>
  </section>
</template>
<script setup>
import { computed, onActivated, onDeactivated, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import SupplierInvoicesView from '@/views/SupplierInvoicesView.vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import PaymentListFilters from '@/components/payments/PaymentListFilters.vue';
import PaymentPagination from '@/components/payments/PaymentPagination.vue';
import '@/assets/styles/payment-lists.css';
import { paymentRequest } from '@/services/supplierPayments';
import { openSupplierPayment } from '@/services/supplierPaymentNavigation';
import { onEvent } from '@/utils/eventBus';
import { sameRecords } from '@/composables/useLiveRefresh';
import '@/assets/supplier-payments.css';
const employeesEnabled =
  import.meta.env.VITE_EMPLOYEE_PAYMENTS_ENABLED !== 'false';
const mode = ref('subcontractors'),
  employees = ref([]),
  items = ref([]),
  meta = ref({}),
  page = ref(1),
  loading = ref(false),
  error = ref('');
const filters = reactive({
  supplierId: '',
  dateFrom: '',
  dateTo: '',
  status: '',
});
let version = 0;
let pendingRequests = 0;
let hasLoaded = false;
let active = false;
let refreshTimer;
const money = (value) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(
    value || 0,
  );
const employeeOptions = computed(() => employees.value.map(employee => ({ ...employee, id: employee.documentId || employee.id })));
const statuses = [{ value: 'unpaid', label: 'Unpaid' }, { value: 'paid', label: 'Paid' }, { value: 'cancelled', label: 'Cancelled' }];
const updateFilters = values => Object.assign(filters, values);
const changePage = value => { page.value = value; void refresh(); };
const formatDate = value => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};
const refresh = async ({ silent = false } = {}) => {
  const request = ++version;
  pendingRequests += 1;
  if (!silent || !hasLoaded) loading.value = true;
  if (!silent) error.value = '';
  try {
    const response = await paymentRequest('', {
      params: { ...filters, page: page.value },
    });
    if (request === version) {
      // A quiet reload only replaces the list when something changed, so the table stays still.
      if (!silent || !sameRecords(items.value, response.data)) items.value = response.data;
      if (!silent || !sameRecords(meta.value, response.meta)) meta.value = response.meta;
      hasLoaded = true;
      error.value = '';
    }
  } catch (cause) {
    if (request === version) error.value = cause.message;
  } finally {
    pendingRequests -= 1;
    if (request === version) loading.value = false;
  }
};
const canRefreshAutomatically = () =>
  active && mode.value === 'employees' && document.visibilityState !== 'hidden';
const refreshAutomatically = () => {
  if (canRefreshAutomatically() && pendingRequests === 0) void refresh({ silent: true });
};
const activate = () => {
  if (active) return;
  active = true;
  window.addEventListener('focus', refreshAutomatically);
  document.addEventListener('visibilitychange', refreshAutomatically);
  refreshTimer = window.setInterval(refreshAutomatically, 30000);
  if (canRefreshAutomatically()) void refresh({ silent: true });
};
const deactivate = () => {
  active = false;
  window.clearInterval(refreshTimer);
  window.removeEventListener('focus', refreshAutomatically);
  document.removeEventListener('visibilitychange', refreshAutomatically);
  version += 1;
  loading.value = false;
};
watch(filters, () => {
  page.value = 1;
  if (mode.value === 'employees') void refresh();
});
watch(mode, () => {
  if (mode.value === 'employees') void refresh();
});
const unsubscribe = onEvent('supplier-payments:changed', () => {
  if (canRefreshAutomatically()) void refresh({ silent: true });
});
const unsubscribeCatalog = onEvent('catalog:changed', async () => {
  try {
    employees.value = (await paymentRequest('/employees')).data;
  } catch (cause) {
    error.value = cause.message;
  }
});
onUnmounted(() => {
  deactivate();
  unsubscribe();
  unsubscribeCatalog();
});
onMounted(async () => {
  activate();
  try {
    employees.value = (await paymentRequest('/employees')).data;
  } catch (cause) {
    error.value = cause.message;
  }
});
onActivated(activate);
onDeactivated(deactivate);
</script>
